import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';

const NAV_ITEMS = [
  { id: 'about', label: 'About', href: '#about' },
  { id: 'skills', label: 'Skills', href: '#skills' },
  { id: 'projects', label: 'Projects', href: '#projects' },
  { id: 'experience', label: 'Experience', href: '#experience' },
  { id: 'contact', label: 'Contact', href: '#contact' },
];

const SCROLL_OFFSET = 80;

export default function ScrollNavigation() {
  const [activeSection, setActiveSection] = useState('about');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 16);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const observers = [];

    NAV_ITEMS?.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(id);
            }
          });
        },
        {
          rootMargin: `-${SCROLL_OFFSET}px 0px -60% 0px`,
          threshold: 0,
        }
      );

      observer?.observe(el);
      observers?.push(observer);
    });

    return () => observers?.forEach((obs) => obs?.disconnect());
  }, []);

  const scrollToSection = useCallback((e, href) => {
    e?.preventDefault();
    const id = href?.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const top = el?.getBoundingClientRect()?.top + window.scrollY - SCROLL_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveSection(id);
    }
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-250 ease-technical ${
          isScrolled
            ? 'border-b' :'border-b border-transparent'
        }`}
        style={{
          height: '72px',
          background: isScrolled ? 'rgba(13,13,15,0.92)' : 'rgba(13,13,15,0.7)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderColor: isScrolled ? 'var(--color-border)' : 'transparent',
        }}
      >
        <div className="max-w-container mx-auto px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <a
            href="#about"
            onClick={(e) => scrollToSection(e, '#about')}
            className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xs"
            aria-label="Rehmat Ullah - Back to top"
          >
            <div
              className="w-9 h-9 flex items-center justify-center rounded-xs transition-all duration-250 ease-technical group-hover:shadow-glow"
              style={{ background: 'var(--color-primary)' }}
            >
              <span
                className="text-sm font-bold leading-none"
                style={{ color: 'var(--color-primary-foreground)', fontFamily: 'var(--font-heading)' }}
              >
                RU
              </span>
            </div>
            <span
              className="hidden sm:block text-sm font-semibold tracking-tight transition-colors duration-250 group-hover:text-primary"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
            >
              Rehmat Ullah
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Portfolio sections">
            {NAV_ITEMS?.map(({ id, label, href }) => {
              const isActive = activeSection === id;
              return (
                <a
                  key={id}
                  href={href}
                  onClick={(e) => scrollToSection(e, href)}
                  className={`relative px-4 py-2 text-sm font-medium rounded-xs transition-all duration-250 ease-technical focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    isActive
                      ? 'text-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                  style={{ fontFamily: 'var(--font-heading)' }}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {label}
                  {isActive && (
                    <span
                      className="absolute bottom-0 left-4 right-4 h-0.5 rounded-none"
                      style={{ background: 'var(--color-primary)' }}
                    />
                  )}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="inline-flex items-center gap-2 px-6 h-10 text-sm font-semibold rounded-xs transition-all duration-250 ease-technical hover:-translate-y-px hover:shadow-md active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              style={{
                background: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                fontFamily: 'var(--font-heading)',
              }}
            >
              <Icon name="Mail" size={16} color="currentColor" />
              Hire Me
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xs border border-border transition-all duration-250 ease-technical hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <Icon
              name={isMobileMenuOpen ? 'X' : 'Menu'}
              size={20}
              color="var(--color-foreground)"
            />
          </button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[1100]"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
          id="mobile-menu"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.9)' }}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Menu Panel */}
          <div
            className="absolute top-0 right-0 w-72 h-full flex flex-col animate-slide-down"
            style={{ background: 'var(--color-card)', borderLeft: '1px solid var(--color-border)' }}
          >
            {/* Panel Header */}
            <div
              className="flex items-center justify-between px-6 h-[72px] border-b"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <span
                className="text-sm font-semibold"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
              >
                Navigation
              </span>
              <button
                className="flex items-center justify-center w-10 h-10 rounded-xs border border-border transition-all duration-250 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={closeMobileMenu}
                aria-label="Close navigation menu"
              >
                <Icon name="X" size={18} color="var(--color-foreground)" />
              </button>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 flex flex-col px-4 py-6 gap-1" aria-label="Mobile portfolio sections">
              {NAV_ITEMS?.map(({ id, label, href }) => {
                const isActive = activeSection === id;
                return (
                  <a
                    key={id}
                    href={href}
                    onClick={(e) => scrollToSection(e, href)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xs transition-all duration-250 ease-technical focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      isActive
                        ? 'text-primary bg-muted' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    style={{ fontFamily: 'var(--font-heading)' }}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {isActive && (
                      <span
                        className="w-1 h-4 rounded-none flex-shrink-0"
                        style={{ background: 'var(--color-primary)' }}
                      />
                    )}
                    {!isActive && <span className="w-1 h-4 flex-shrink-0" />}
                    {label}
                  </a>
                );
              })}
            </nav>

            {/* Mobile CTA */}
            <div className="px-4 pb-8">
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="flex items-center justify-center gap-2 w-full h-12 text-sm font-semibold rounded-xs transition-all duration-250 ease-technical hover:shadow-md active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{
                  background: 'var(--color-primary)',
                  color: 'var(--color-primary-foreground)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                <Icon name="Mail" size={16} color="currentColor" />
                Hire Me
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}